import { useState, useEffect } from 'react'
import { useToast } from '../context/ToastContext'
import { apiCall } from '../utils/api'
import AddPaymentMethodModal from './modals/AddPaymentMethodModal'
import { CREDIT_PACKAGES } from './pricing/PricingPackages'

export default function AutoReloadSettings() {
  const { showToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const [hasConsentedBefore, setHasConsentedBefore] = useState(false)

  const [settings, setSettings] = useState({
    enabled: false,
    threshold: 5,
    packageId: ''
  })
  const [legalConsent, setLegalConsent] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [userResponse, pmResponse] = await Promise.all([
        apiCall('/v1/user/me'),
        apiCall('/v1/user/payment-methods')
      ])

      if (!userResponse.ok || !pmResponse.ok) {
        throw new Error('Failed to load data')
      }

      const userData = await userResponse.json()
      const pmData = await pmResponse.json()

      // Load auto-reload settings and find matching package
      const amountUsd = userData.auto_reload_amount_usd || 0
      const matchingPackage = CREDIT_PACKAGES.find(pkg => pkg.credits === amountUsd)

      setSettings({
        enabled: userData.auto_reload_enabled || false,
        threshold: userData.auto_reload_threshold_usd || 5,
        packageId: matchingPackage ? matchingPackage.id : ''
      })

      // Check if user has consented before
      if (userData.auto_reload_consent_accepted_at) {
        setHasConsentedBefore(true)
        setLegalConsent(true) // Auto-check if they've consented before
      }

      // Load payment methods and auto-select the DEFAULT one
      const methods = pmData.paymentMethods || []
      setPaymentMethods(methods)

      if (methods.length > 0) {
        // Find and select the default payment method
        const defaultMethod = methods.find(pm => pm.is_default)
        setSelectedPaymentMethod(defaultMethod ? defaultMethod.id : methods[0].id)
      }
    } catch (error) {
      console.error('Error loading auto-reload data:', error)
      showToast('error', 'Failed to load settings', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    // Validations
    if (settings.enabled && paymentMethods.length === 0) {
      showToast('error', 'Please add a payment method first', 'Payment Method Required')
      return
    }

    // Only require consent if user hasn't consented before
    if (settings.enabled && !legalConsent && !hasConsentedBefore) {
      showToast('error', 'Please accept the authorization agreement', 'Consent Required')
      return
    }

    if (settings.enabled && !settings.packageId) {
      showToast('error', 'Please select a reload package', 'Package Required')
      return
    }

    try {
      setSaving(true)

      const selectedPkg = CREDIT_PACKAGES.find(p => p.id === settings.packageId)
      const amountUsd = selectedPkg ? selectedPkg.credits : 20

      const response = await apiCall('/v1/user/auto-reload', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: settings.enabled,
          threshold_usd: parseFloat(settings.threshold),
          amount_usd: amountUsd,
          package_id: settings.packageId
        })
      })

      if (response.ok) {
        showToast('success', 'Auto-reload settings saved successfully', 'Success')
        // Reload data to get updated settings from server
        await loadData()
      } else {
        const errorData = await response.json()
        showToast('error', errorData.message || 'Failed to save settings', 'Error')
      }
    } catch (error) {
      console.error('Error saving auto-reload settings:', error)
      showToast('error', 'Failed to save settings', 'Error')
    } finally {
      setSaving(false)
    }
  }

  const handleAddPaymentSuccess = () => {
    setShowAddPaymentModal(false)
    loadData()
    showToast('success', 'Payment method added successfully', 'Success')
  }

  const getCardBrandIcon = (brand) => {
    const brandIcons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    }
    return brandIcons[brand?.toLowerCase()] || '💳'
  }

  const formatCardBrand = (brand) => {
    if (!brand) return 'Card'
    return brand.charAt(0).toUpperCase() + brand.slice(1)
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Auto-Reload Settings</h2>

      <div className="space-y-6">
        {/* Payment Method Section - FIRST, BEFORE TOGGLE */}
        <div className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-800 dark:to-gray-900 border-2 border-primary-200 dark:border-primary-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-2xl">💳</span>
                Payment Method
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                This card will be charged when auto-reload triggers
              </p>
            </div>
          </div>

          {paymentMethods.length === 0 ? (
            /* No Payment Methods */
            <div className="bg-white dark:bg-gray-900/50 rounded-lg p-6 text-center border-2 border-dashed border-gray-300 dark:border-gray-600">
              <div className="text-4xl mb-3">💳</div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                No Payment Method Saved
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Add a payment method to enable automatic reloading
              </p>
              <button
                onClick={() => setShowAddPaymentModal(true)}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
              >
                <span className="text-xl">+</span>
                Add Payment Method
              </button>
            </div>
          ) : (
            /* Payment Methods Grid */
            <div className="space-y-3">
              {paymentMethods.map(pm => (
                <div
                  key={pm.id}
                  onClick={() => setSelectedPaymentMethod(pm.id)}
                  className={`bg-white dark:bg-gray-900/50 rounded-lg p-4 cursor-pointer transition-all transform hover:scale-102 border-2 ${
                    selectedPaymentMethod === pm.id
                      ? 'border-primary-500 shadow-lg ring-2 ring-primary-200 dark:ring-primary-800'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{getCardBrandIcon(pm.card_brand)}</div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {formatCardBrand(pm.card_brand)} •••• {pm.card_last4}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Expires {pm.card_exp_month?.toString().padStart(2, '0')}/{pm.card_exp_year}
                        </div>
                      </div>
                    </div>
                    {selectedPaymentMethod === pm.id && (
                      <div className="text-primary-600 dark:text-primary-400 text-2xl">
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button
                onClick={() => setShowAddPaymentModal(true)}
                className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg font-medium transition-all"
              >
                + Add Another Payment Method
              </button>
            </div>
          )}
        </div>

        {/* Enable/Disable Toggle */}
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Auto-Reload Status</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Automatically recharge when balance is low</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                disabled={paymentMethods.length === 0}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer disabled:opacity-50 disabled:cursor-not-allowed peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
          {paymentMethods.length === 0 && (
            <div className="mt-3 text-sm text-orange-600 dark:text-orange-400">
              ⚠️ Add a payment method to enable auto-reload
            </div>
          )}
        </div>

        {/* Configuration (only show when enabled) */}
        {settings.enabled && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reload When Balance Falls Below
              </label>
              <select
                value={settings.threshold}
                onChange={(e) => setSettings(prev => ({ ...prev, threshold: e.target.value }))}
                className="w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value="5">$5.00</option>
                <option value="10">$10.00</option>
                <option value="20">$20.00</option>
                <option value="50">$50.00</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reload Amount
              </label>
              <select
                value={settings.packageId}
                onChange={(e) => setSettings(prev => ({ ...prev, packageId: e.target.value }))}
                className="w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value="">Select a package</option>
                {CREDIT_PACKAGES.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    ${pkg.price} → ${pkg.credits} credits {pkg.bonus > 0 && `(+${Math.round((pkg.bonus / pkg.price) * 100)}% bonus)`}
                  </option>
                ))}
              </select>
            </div>

            {/* Legal Consent Checkbox - Only show if user hasn't consented before */}
            {!hasConsentedBefore && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={legalConsent}
                    onChange={(e) => setLegalConsent(e.target.checked)}
                    className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Authorization Agreement
                    </div>
                    <div className="text-blue-800 dark:text-blue-200">
                      I authorize ContentIndexer to automatically charge my saved payment method when my account balance falls below ${settings.threshold}.00, in the amount specified above. I understand that I can disable auto-reload at any time and will receive email notifications before each charge.
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* How it Works Info */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">ℹ️</div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <p className="font-semibold mb-2">How Auto-Reload Works:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• We'll charge your selected payment method when balance drops below ${settings.threshold}.00</li>
                    <li>• You'll receive an email notification before each reload</li>
                    <li>• You can disable auto-reload anytime</li>
                    <li>• Same bonus credits apply as regular purchases!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving || (settings.enabled && paymentMethods.length === 0)}
            className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 active:scale-95 disabled:transform-none shadow-lg disabled:shadow-none"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-3">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </span>
            ) : (
              'Save Auto-Reload Settings'
            )}
          </button>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddPaymentModal && (
        <AddPaymentMethodModal
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={handleAddPaymentSuccess}
        />
      )}
    </div>
  )
}
